import { cn, Text } from "@aurem/ui";
import { use } from "react";
import { useTranslations } from "../i18n";
import { LanguageCurrencyPickerContext } from "./context";
import type { Language, TranslationMap } from "./types";

type LanguageTabProps = {
  languages: Language[];
  currentLanguage: string;
  onLanguageSelect: (code: string) => void;
};

const LanguageTab = ({
  languages,
  currentLanguage,
  onLanguageSelect,
}: LanguageTabProps) => {
  const { t } = useTranslations();
  const { setIsOpen, supportedLanguages } = use(LanguageCurrencyPickerContext);

  const translationMap: TranslationMap = {
    debug: {
      label: "Debug",
      description: "",
    },
    ...supportedLanguages.reduce<
      Record<string, { label: string; description: string }>
    >((acc, lang) => {
      acc[lang] = {
        label: t(`components.languageCurrencyPicker.language.${String(lang)}`),
        description: t(
          `components.languageCurrencyPicker.language.${String(lang)}Description`,
        ),
      };
      return acc;
    }, {}),
  };

  const onSelect = (id: string) => {
    return () => {
      onLanguageSelect(id);
      setIsOpen(false);
    };
  };

  return (
    <div className="flex flex-col gap-4">
      <Text size="md" weight="medium">
        {t("components.languageCurrencyPicker.chooseDefaultLanguage")}
      </Text>
      <div className="flex flex-col gap-2">
        {languages.map((language) => (
          <button
            key={language.code}
            onClick={onSelect(language.code)}
            className={cn(
              "flex justify-between items-center px-4 rounded-xl h-[52px]",
              "border border-neutral-500/5 hover:bg-neutral-500/5 focus-visible:bg-neutral-500/5 active:bg-neutral-500/10",
              "focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
              currentLanguage === language.code && "border-primary-500",
            )}
            style={{ outlineOffset: "1px" }}
          >
            <Text as="span" size="md">
              {translationMap[language.code].label}
            </Text>
            <Text as="span" size="sm" className="text-neutral-500/50">
              {translationMap[language.code].description}
            </Text>
          </button>
        ))}
      </div>
    </div>
  );
};

export default LanguageTab;
